import { useQuery, UseQueryResult } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { useProjectStore } from "stores/projectStore"
import {
  getQueryLineage,
  getProjectLineage,
  getQnrMetadata,
  getDeliverableLineage,
} from "api/Engine/lineage"
import { getQueries } from "api/Repos/queries"
import { LineageDag, QnrMetadata } from "types/lineage.types"
import { unionLineageDags } from "utils/lineageUnion"
import { useProjectOwner } from "./useProjectOwner"

/**
 * The names of the open project's saved queries, to pick one to trace. Saving or deleting a
 * saved query reads them again. Silent: the lineage section says inline when they couldn't be
 * read. `enabled: false` reads nothing (the Tables screen reads them only while a proposal
 * waits on a script).
 */
export const useSavedQueryNamesQuery = (
  projectId: string,
  enabled = true
): UseQueryResult<string[]> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.savedQueries.names(projectId, owner),
    enabled: enabled && !!projectId && !!owner,
    meta: { silent: true },
    // The server answers the names one per line.
    queryFn: async (): Promise<string[]> =>
      ((await getQueries(await getAxios(), projectId, owner)).data || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
  })
}

/**
 * The lineage graph of a saved query, rebuilt on the server (it loads the project's data and
 * runs the query again). Reads nothing until a query is picked. Not retried: a replay that
 * failed once fails again, and each try runs the whole query. Silent: the lineage section says
 * inline when it failed.
 */
export const useLineageQuery = (
  projectId: string,
  query: string | undefined
): UseQueryResult<LineageDag> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.lineage(projectId, owner, query ?? ""),
    enabled: !!projectId && !!owner && !!query,
    retry: false,
    meta: { silent: true },
    queryFn: async (): Promise<LineageDag> =>
      (await getProjectLineage(await getAxios(), projectId, owner, query)).data,
  })
}

/**
 * Runs a request on a live session in its turn with the session's other engine calls, and lets
 * nothing else run there until it has settled. The engine does not serialise the calls of one
 * session: a read of the lineage sent while a script runs mixes their answers or stops it. The
 * feature that holds the session supplies it: the tab spec's and the dashboard's queue, the
 * console's wait for its run to end.
 */
export type RunExclusive = <T>(task: () => Promise<T>) => Promise<T>

/**
 * The lineage graph of a live console session. The session changes with every command, so it is
 * read again each time it is enabled (staleTime 0); show `isFetching` rather than old data.
 * With `runExclusive` the read waits its turn with the session's other calls. Silent: the
 * lineage modal says inline when it failed.
 */
export const useSessionLineageQuery = (
  instanceId: string | null,
  enabled: boolean = true,
  runExclusive?: RunExclusive
): UseQueryResult<LineageDag> => {
  const getAxios = useAuthedAxios()
  return useQuery({
    queryKey: queryKeys.provenance.sessionLineage(instanceId ?? ""),
    enabled: enabled && !!instanceId,
    staleTime: 0,
    meta: { silent: true },
    queryFn: (): Promise<LineageDag> => {
      const read = async (): Promise<LineageDag> =>
        (await getQueryLineage(await getAxios(), instanceId as string)).data ?? {
          nodes: [],
          edges: [],
        }
      return runExclusive ? runExclusive(read) : read()
    },
  })
}

/**
 * The questionnaire's fields and how the data differs from them, shown beside a collected
 * variable in the lineage graph. Advisory (the graph shows without it), so silent.
 */
export const useQuestionnaireMetadataQuery = (
  projectId: string,
  enabled: boolean = true
): UseQueryResult<QnrMetadata> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.qnrMetadata(projectId, owner),
    enabled: enabled && !!projectId && !!owner,
    meta: { silent: true },
    queryFn: async (): Promise<QnrMetadata> =>
      (await getQnrMetadata(await getAxios(), projectId, owner)).data,
  })
}

/**
 * The lineage graph captured when an export of the open project was recorded, by its hash. The
 * hash names the content, so a graph once read never changes. Silent: its viewers say inline
 * when it couldn't be read.
 */
export const useDeliverableLineageQuery = (
  hash: string | null | undefined
): UseQueryResult<LineageDag> => {
  const getAxios = useAuthedAxios()
  const projectId = useProjectStore((s) => s.project.id)
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.deliverableLineage(projectId, owner, hash ?? ""),
    enabled: !!projectId && !!owner && !!hash,
    staleTime: Infinity,
    meta: { silent: true },
    queryFn: async (): Promise<LineageDag> =>
      (await getDeliverableLineage(await getAxios(), projectId, owner, hash as string)).data,
  })
}

/**
 * The lineage a merged file inherits from its sources: the graphs captured for each side when
 * the files were merged (pinned on the `data.merged` record), combined. A union, not a
 * concatenation: the same derive script normally ran on both waves, so identical edges collapse
 * and only real per-wave differences stay. A side whose graph can't be read is left out; the
 * query fails only when none could be read. Silent: the lineage section says so inline.
 */
export const useInheritedLineageQuery = (
  projectId: string,
  hashes: string[]
): UseQueryResult<LineageDag> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.deliverableLineage(projectId, owner, `inherited:${hashes.join("+")}`),
    enabled: !!projectId && !!owner && hashes.length > 0,
    staleTime: Infinity,
    meta: { silent: true },
    queryFn: async (): Promise<LineageDag> => {
      const axios = await getAxios()
      const dags = await Promise.all(
        hashes.map((hash) =>
          getDeliverableLineage(axios, projectId, owner, hash)
            .then((res) => res.data ?? null)
            .catch(() => null)
        )
      )
      const merged = unionLineageDags(dags)
      if (!merged.nodes.length) throw new Error("No captured lineage could be read")
      return merged
    },
  })
}
